import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { trigger, transition, style, animate, query, stagger, group } from '@angular/animations';
import { NotificationService } from '../notification-service/notification.service';
import { Notification } from './notification.interface';
import { NotificationItemComponent } from './notification-item.component';

/**
 * Container component for displaying multiple notifications
 * Positioned on the right side of the screen, stacked vertically
 */
@Component({
	selector: 'app-notification-container',
	standalone: true,
	imports: [CommonModule, NotificationItemComponent],
	templateUrl: './notification-container.component.html',
	styleUrls: ['./notification-container.component.scss'],
	animations: [
		trigger('listAnimation', [
			transition('* => *', [
				// First: Apply initial style to entering items immediately
				query(':enter', [
					style({ opacity: 0, transform: 'translateX(400px)' })
				], { optional: true }),
				// Then: Run all animations in parallel
				group([
					// Animate existing items to their new position
					query(':not(:enter):not(:leave)', [
						animate('500ms ease-out')
					], { optional: true }),
					// Animate entering items
					query(':enter', [
						stagger(50, [
							animate(
								'200ms ease-out',
								style({ opacity: 1, transform: 'translateX(0)' })
							),
						]),
					], { optional: true }),
					// Animate leaving items
					query(':leave', [
						stagger(50, [
							animate(
								'200ms ease-in',
								style({ opacity: 0, transform: 'translateX(400px)' })
							),
						]),
					], { optional: true }),
				]),
			]),
		]),
	],
})
export class NotificationContainerComponent implements OnDestroy {
	notifications: Notification[] = [];
	isVisible: boolean = true;
	private notificationsSubscription: Subscription;
	private visibilitySubscription: Subscription;

	constructor(private notificationService: NotificationService) {
		// Subscribe to notifications from service
		this.notificationsSubscription = this.notificationService.notifications$.subscribe(
			notifications => {
				this.notifications = notifications;
			}
		);

		// Subscribe to visibility state
		this.visibilitySubscription = this.notificationService.containerVisible$.subscribe(
			isVisible => {
				this.isVisible = isVisible;
			}
		);
	}

	ngOnDestroy(): void {
		// Unsubscribe when component is destroyed
		if (this.notificationsSubscription) {
			this.notificationsSubscription.unsubscribe();
		}
		if (this.visibilitySubscription) {
			this.visibilitySubscription.unsubscribe();
		}
	}

	/**
	 * Handle notification close event from child component
	 * @param notificationId - ID of the notification to close
	 */
	onCloseNotification(notificationId: string): void {
		// Call the service's close method directly
		// The service will handle removing the notification from the list
		this.notificationService.closeNotificationById(notificationId);
	}

	/**
	 * Track notifications by ID for Angular's *ngFor optimization
	 */
	trackByNotificationId(index: number, notification: Notification): string {
		return notification.id;
	}
}
